<template>
  <div>
    <div class="grid bg-white">
      <div class="row grid-body">
        <div class="col-md-12 header-row">
          <h3 class="heading semi-bold">Shirt Order breakdown</h3>
          <div class="row">
            <div class="col-md-3">
              <label>Date From</label>
              <datepicker
                :clear-button="true"
                placeholder="Date From"
                v-model="dateFrom"
                input-class="form-control"
                :disabled-dates="disabledDatesMinRange"
                @selected="minDateSelected()"
              >
              </datepicker>
            </div>
            <div class="col-md-3">
              <label>Date To</label>
              <datepicker
                :clear-button="true"
                placeholder="Date To"
                v-model="dateTo"
                input-class="form-control"
                :disabled-dates="disabledDatesMaxRange"
                @selected="maxDateSelected()"
              >
              </datepicker>
            </div>
            <div class="col-md-2">
              <label>Timezone</label>
              <v-select
                :options="timezones"
                v-model="selectedTimezone"
              ></v-select>
            </div>
            <div class="col-md-2">
              <button
                class="btn btn-success filter-btn"
                @click="getDailyBreakdown()"
              >
                <i class="fa fa-search" aria-hidden="true"></i> Filter
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="row grid-body">
        <div class="table-responsive">
          <table border="1">
            <thead>
              <tr>
                <th></th>
                <th></th>
                <th colspan="3">Male</th>
                <th colspan="3">Lady</th>
                <th colspan="3">Dress</th>
              </tr>
              <tr>
                <th></th>
                <th>Totals:</th>
                <th colspan="3">{{ totals.male.all }}</th>
                <th colspan="3">{{ totals.lady.all }}</th>
                <th colspan="3">{{ totals.dress.all }}</th>
              </tr>
              <tr>
                <th></th>
                <th></th>
                <th>Basic</th>
                <th>Premium</th>
                <th>Luxury</th>
                <th>Basic</th>
                <th>Premium</th>
                <th>Luxury</th>
                <th>Basic</th>
                <th>Premium</th>
                <th>Luxury</th>
              </tr>
              <tr>
                <th></th>
                <th>Sub-totals:</th>
                <th>{{ totals.male.basic }}</th>
                <th>{{ totals.male.premium }}</th>
                <th>{{ totals.male.luxury }}</th>
                <th>{{ totals.lady.basic }}</th>
                <th>{{ totals.lady.premium }}</th>
                <th>{{ totals.lady.luxury }}</th>
                <th>{{ totals.dress.basic }}</th>
                <th>{{ totals.dress.premium }}</th>
                <th>{{ totals.dress.luxury }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in orders">
                <td>{{ order.date }}</td>
                <td>
                    {{
                        order.male.basic + order.male.premium + order.male.luxury +
                        order.lady.basic + order.lady.premium + order.lady.luxury +
                        order.dress.basic + order.dress.premium + order.dress.luxury

                    }}
                </td>
                <td>{{ order.male.basic }}</td>
                <td>{{ order.male.premium }}</td>
                <td>{{ order.male.luxury }}</td>
                <td>{{ order.lady.basic }}</td>
                <td>{{ order.lady.premium }}</td>
                <td>{{ order.lady.luxury }}</td>
                <td>{{ order.dress.basic }}</td>
                <td>{{ order.dress.premium }}</td>
                <td>{{ order.dress.luxury }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Datepicker from "vuejs-datepicker";

export default {
  name: "DailyBreakdown",
  components: {
    Datepicker,
  },
  computed: {
    /**
     * On the first date picker, this will allow to select dates between:
     * - 12 months before the selected date on the second date picker
     * - and the date selected on the  second date picker
     */ 
    disabledDatesMinRange () {
      let currentDate = this.dateTo ? new Date(this.dateTo.getTime()) : new Date();
      let minDate = new Date(currentDate.getTime());
      minDate.setMonth(minDate.getMonth() - this.maxMonthRange); 

      return {
        to: minDate,
        from: this.dateTo
      }
    },
    /**
     * On the second date picker, this will allow the user a date
     * up to the current date. There is no limitation for the minimum date
     * to allow the user to select a date far in the cast.
     */ 
    disabledDatesMaxRange () {
      return {
        to: null,  // NO 
        from: new Date() // Do not allow date in the future
      }
    }
  },
  data() {
    return {
      isLoading: false,
      dateFrom: null,
      dateTo: null,
      dateToday: null,
      selectedTimezone: null,
      orders: [],
      timezones: [
        { value: "UTC", label: "Server Time" },
        { value: "Europe/Berlin", label: "Germany" },
        { value: "Asia/Bangkok", label: "Thailand" },
      ],
      totals: {
        male: {
          all: 0,
          basic: 0,
          premium: 0,
          luxury: 0,
        },
        lady: {
          all: 0,
          basic: 0,
          premium: 0,
          luxury: 0,
        },
        dress: {
          all: 0,
          basic: 0,
          premium: 0,
          luxury: 0,
        },
      },
      maxDateChanges: 0,
      maxMonthRange: 12
    };
  },
  mounted() {
    // Initialize the date range
    this.dateFrom = new Date(new moment().startOf("today").subtract(7, "days"));
    this.dateTo = new Date(new moment().endOf("today"));
    this.dateToday = new Date(new moment().startOf("today"));
    this.selectedTimezone = this.timezones.filter(
      (tz) => tz.value === "Asia/Bangkok"
    )[0];

    this.getDailyBreakdown();
  },
  methods: {
    async getDailyBreakdown() {
      const dateFrom = this.dateFrom
        ? this.dateFrom.toISOString().split("T")[0]
        : null;
      const dateTo = this.dateTo
        ? this.dateTo.toISOString().split("T")[0]
        : null;

      const params = {
        start: dateFrom,
        end: dateTo,
        timezone: this.selectedTimezone.value,
      };

      try {
        let response = await axios.get("/shirt_orders/daily_breakdown", {
          params: params,
        });
        this.orders = response.data.data;
        this.computeTotals();
      } catch (error) {
        if (error.response.status === 403) {
          toastr.error(
            "Not authorized, please ensure that you have the required permissions."
          );
        }
      }
    },
    computeTotals() {
      // Reset the totals
      this.totals = {
        male: {
          all: 0,
          basic: 0,
          premium: 0,
          luxury: 0,
        },
        lady: {
          all: 0,
          basic: 0,
          premium: 0,
          luxury: 0,
        },
        dress: {
          all: 0,
          basic: 0,
          premium: 0,
          luxury: 0,
        },
      };

      /**
       * `this.orders` is indexed by date, and we need to get an array representation of values
       *  of the object in order to be able to use the `forEach` method, and loop over the values only to ca  lculate the totals.
       */
      Object.values(this.orders).forEach((order) => {
        this.totals.male.basic += order.male.basic;
        this.totals.male.premium += order.male.premium;
        this.totals.male.luxury += order.male.luxury;
        this.totals.male.all =
          this.totals.male.basic +
          this.totals.male.premium +
          this.totals.male.luxury;

        this.totals.lady.basic += order.lady.basic;
        this.totals.lady.premium += order.lady.premium;
        this.totals.lady.luxury += order.lady.luxury;
        this.totals.lady.all =
          this.totals.lady.basic +
          this.totals.lady.premium +
          this.totals.lady.luxury;

        this.totals.dress.basic += order.dress.basic;
        this.totals.dress.premium += order.dress.premium;
        this.totals.dress.luxury += order.dress.luxury;
        this.totals.dress.all =
          this.totals.dress.basic +
          this.totals.dress.premium +
          this.totals.dress.luxury;
      });
    },
    minDateSelected() {
      /**
       * The selected event is fired before the Vue nextTick function which updates the v-model.
       * So we need to handle the change inside the `nextTick` to get the correct value.
       */ 
      this.$nextTick(() => { 
        /**
         * Min date should not be bigger than the current date
         */ 
        let currentDate = new Date();
        if(this.dateFrom.getTime() > currentDate.getTime()) {
          this.dateFrom = currentDate;
        }

        /**
         * Max date should not be bigger than minDate plus `maxMonthRange`
         */ 
        let maxDate = new Date(this.dateTo);
        maxDate.setMonth(maxDate.getMonth() + this.maxMonthRange);
        if(this.dateTo.getTime() > maxDate.getTime()) {
          this.dateTo = new Date(maxDate);
        }
      });
    },
    maxDateSelected() {
      /**
       * @see above comment.
       */ 
      this.$nextTick(() => { 

        /**
         * Max date should not be bigger than the current date
         */ 
        let currentDate = new Date();
        if(this.dateTo.getTime() > currentDate.getTime()) {
          this.dateTo = currentDate;
        }

        /**
         * Min date should not be bigger than max date.
         */
        if(this.dateFrom.getTime() > this.dateTo.getTime()) {
          this.dateFrom = new Date(moment(this.dateTo).startOf("today").subtract(7, "days"));
        }
      });
    }
  },
};
</script>

<style lang="css" scoped>
.filter-btn {
  margin-top: 27px;
}
th {
  text-align: center;
}
h4 {
  margin-top: 7px;
}
table {
  table-layout: fixed !important;
  max-width: none;
  width: 100%;
  border-collapse: separate !important; /* Don't collapse */
  border-spacing: 0 !important;
  border-left: 1px solid #d7d8da;
  border-right: 1px solid #d7d8da;
  border-top: none;
  border-bottom: none;
}

th,
td {
  padding: 5px !important;
  border: 1px solid #d7d8da;
  border-left: none;
  border-top: none;
}
td:last-child,
th:last-child {
  border-right: none;
}

.table-responsive {
  height: 700px;
  overflow: scroll;
}
th {
  position: sticky;
  top: -1px;
  z-index: 1;
  background: var(--primary-color);
  color: white;
}

tbody tr td {
  text-align: center;
}
</style>
